import React, { useState } from 'react';
import { Alert, Stack, TextField } from '@mui/material';

import { closeCurrentPopup, Dialog } from '@paragon/popup-state';

import { createLine, patchLine } from '../../actions/supervisorActions';
import { useNavigateIfNeeded } from '../../util/useNavigateIfNeeded';
import { useAppDispatch, useAppSelector } from '../hooks';
import { ParentSelect } from '../interface/ParentSelect';
import { EditLineDialogProps } from './dialogs';

export function EditLineDialog(props: EditLineDialogProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigateIfNeeded();

  const linesState = useAppSelector((state) => state.lines);

  const [didFail, setDidFail] = useState(false);

  let name: string | undefined;
  const [editedName, setEditedName] = useState<string>();
  const [nameProblem, setNameProblem] = useState<string>();

  let parentGuid: string | undefined;
  const [editedParentGuid, setEditedParentGuid] = useState<string | null | undefined>();
  function applyEditedParentGuid(currentParentGuid?: string): string | undefined {
    // null means the existing parent guid has been removed by user selection.
    if (editedParentGuid === null) return undefined;
    return editedParentGuid ?? currentParentGuid;
  }

  if (props.mode === 'create') {
    name = editedName;
    parentGuid = applyEditedParentGuid(props.parentGuid);
  } else {
    const line = linesState.lines[props.lineGuid];
    if (linesState.status !== 'loaded' || line == null) return null;

    name = editedName ?? line.name;
    parentGuid = applyEditedParentGuid(line.parentGuid);
  }

  async function onSubmit() {
    if (props.mode === 'edit' && !editedName && !editedParentGuid) {
      dispatch(closeCurrentPopup());
      return;
    }

    if (name == null || name.trim() === '') {
      setNameProblem('name is required');
      return;
    }

    setDidFail(false);
    setNameProblem(undefined);

    try {
      switch (props.mode) {
        case 'create':
          await dispatch(createLine(name, parentGuid));
          break;

        case 'edit':
          await dispatch(patchLine(props.lineGuid, { name: editedName, parentGuid: editedParentGuid }));
          break;
      }
    } catch (error) {
      setDidFail(true);

      console.error(error);

      return;
    }

    dispatch(closeCurrentPopup());

    if (props.mode === 'create' && parentGuid) {
      navigate(`/directories/${parentGuid}`);
    }
  }

  const actionString = props.mode === 'create' ? 'Create' : 'Edit';

  return (
    <Dialog
      title={`${actionString} line`}
      submitText={actionString}
      onSubmit={onSubmit}
      onCancel="close"
      enterKeySubmits
    >
      <Stack spacing={2} sx={{ marginTop: 1 }}>
        {didFail && (
          <Alert variant="outlined" severity="error">
            Unable to {actionString.toLowerCase()} line.
          </Alert>
        )}

        <TextField
          autoFocus
          autoComplete="off"
          size="small"
          label="Line Name"
          sx={{ marginTop: 1 }}
          value={name}
          error={!!nameProblem}
          onChange={(event) => setEditedName(event.target.value)}
          onFocus={(event) => {
            event.target.select();
          }}
        />

        <ParentSelect
          includeDirectories
          label="Directory"
          optional={props.mode === 'edit' || !parentGuid}
          parentGuid={parentGuid}
          onChange={setEditedParentGuid}
        />
      </Stack>
    </Dialog>
  );
}
